<template>
  <div class="home bg-light">
    <SiteNav :login-user="loginUser"/>
    <nav class="navbar navbar-expand sticky-top navbar-light bg-light border-bottom">
      <div class="container-fluid container-3xl justify-content-start">
        <div class="row row-cols-auto">
          <div class="col col-12 col-md-auto">
            <multiselect
              v-model="projectSelectValue"
              :options="projectSelectOptions"
              label="name"
              track-by="id"
              :allow-empty="false"
              :show-labels="false"
              placeholder="🔍  輸入關鍵字搜尋專案 ..."
              class="project-select"
              @select="filterTimeEntriesByProject"
            ><span slot="noResult">找不到符合的專案</span></multiselect>
          </div>
          <div class="col col-6 col-md-auto">
            <multiselect
              v-model="userSelectValue"
              :options="userSelectOptions"
              :loading="userSelectOptionsLoading"
              :custom-label="({ first_name, last_name }) => `${first_name} ${last_name}`"
              track-by="id"
              :allow-empty="false"
              :show-labels="false"
              placeholder="🔍  輸入成員"
              class="user-select"
              @select="filterTimeEntriesByUser"
            ><span slot="noResult">找不到符合的成員</span></multiselect>
          </div>
          <div
            class="col d-none d-md-flex align-items-center toggle-date-range"
            role="button"
            @click="yearAndMonthOrDateRange = !yearAndMonthOrDateRange"
          >
            <span>
              <i class="bi" :class="{
                'bi-calendar'      :  yearAndMonthOrDateRange,
                'bi-calendar-range': !yearAndMonthOrDateRange,
              }"></i>
            </span>
          </div>
          <div class="col col-6 col-md-auto" v-show="yearAndMonthOrDateRange">
            <!-- REMARK: Using <input type="month"> is more appropriate.
             Unfortunately, Desktop Safari doesn't support it (as of Mar 2022). -->
            <multiselect
              v-model="yearAndMonthSelectValue"
              :options="yearAndMonthSelectOptions"
              :allow-empty="false"
              :show-labels="false"
              placeholder="🔍  輸入年月"
              class="year-and-month-select"
              @open="scrollToSelectedOption"
            ><span slot="noResult">
              <span class="text-multiline">請輸入介於<br>
              {{ yearAndMonthSelectOptions[0] }} ～<br>
              {{ yearAndMonthSelectOptions[yearAndMonthSelectOptions.length - 1] }} 的月份</span>
            </span></multiselect>
          </div>
          <div class="col col-6 col-md-auto" v-show="!yearAndMonthOrDateRange">
            <DatePicker
              v-model="datePickerSelectValue"
              :model-config="datePickerConfig"
              is-range
              :masks="{ title: 'YYYY年 MMMM' }"
              trim-weeks
              is-required
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div class="input-group">
                  <input
                    class="form-control date-range-select"
                    :value="inputValue.start"
                    v-on="inputEvents.start"
                  />
                  <span class="input-group-text">～</span>
                  <input
                    class="form-control date-range-select"
                    :value="inputValue.end"
                    v-on="inputEvents.end"
                  />
                </div>
              </template>
            </DatePicker>
          </div>
          <div class="col d-none d-md-flex align-items-center">
            <span class="navbar-brand py-0 mx-0">
              <span class="small">總時數</span>&nbsp;
              {{ Math.trunc(totalDuration['*'] / 60) }} 時
              {{ (totalDuration['*'] % 60).toString().padStart(2, '0') }} 分
            </span>
          </div>
          <div class="col d-none d-md-flex align-items-center">
            <!-- eslint-disable-next-line max-len -->
            <button id="export-time-entry-button" class="btn btn-outline-primary btn-sm" type="button">
              <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true">
              </span>
              匯出
            </button>
          </div>
        </div>
      </div>
    </nav>
    <div class="container-fluid container-3xl margin-with-bottom-navbar">
      <div class="row">
        <main class="col-12 col-md-7">
          <div v-if="yearAndMonthOrDateRange" class="d-flex justify-content-between px-2 pb-2">
            <h5 class="fw-bold">{{ yearAndMonthSelectValue }}</h5>
            <div class="btn-group btn-group-sm">
              <button class="btn btn-outline-secondary" type="button" @click="goPrevMonth"
                :disabled="isVeryFirstMonth"><i class="bi bi-chevron-left"></i></button>
              <button class="btn btn-outline-secondary" type="button" @click="goNextMonth"
                :disabled="isVeryLastMonth"><i class="bi bi-chevron-right"></i></button>
            </div>
          </div>
          <TimeEntrySummary
            v-if="yearAndMonthOrDateRange"
            :year="parseInt(yearAndMonthSelectValue.split('/')[0], 10)"
            :month="parseInt(yearAndMonthSelectValue.split('/')[1], 10)"
            :total-duration="totalDuration"
            :selected-date.sync="selectedDate"
          ></TimeEntrySummary>
          <table v-if="timeEntries.length > 0" class="table table-hover">
            <TimeEntry
              v-for="timeEntry in timeEntries"
              :key="timeEntry.id"
              :id="timeEntry.id"
              :user-first-name="timeEntry.user_first_name"
              :user-last-name="timeEntry.user_last_name"
              :user-color="timeEntry.userColor"
              :project-name="timeEntry.project_name"
              :service-name="timeEntry.service_name"
              :description="timeEntry.description"
              :date="timeEntry.date"
              :day-of-week="timeEntry.dayOfWeek"
              :duration="timeEntry.duration"
              :same-date-as-above="timeEntry.sameDateAsAbove"
              :total-duration="totalDuration[timeEntry.date]"
              :editable="timeEntry.user === loginUser.id &&
                projectFormOptions.some((project) => project.id === timeEntry.project)"
              :highlighted="timeEntry.highlighted"
              v-show="(selectedDate === null) || (timeEntry.date === selectedDate)"
              @copy="copyTimeEntry"
            ></TimeEntry>
          </table>
          <div v-else class="py-5 text-center">目前還沒有時數紀錄唷 🍂</div>
        </main>
        <aside class="col-12 col-md-5 d-none d-md-block">
          <div class="card card-shadow sticky-md-top sticky-after-navbar">
            <div class="card-body">
              <h5 class="card-title fw-bold">新增時數</h5>
              <form class="row gx-4 gy-3" @submit.prevent="createTimeEntry">
                <div v-show="errorFormColumns.length > 0" class="col-12">
                  <div class="alert alert-danger mb-0 d-flex align-items-center" role="alert">
                    <span class="me-12px"><i class="bi bi-exclamation-circle"></i></span>
                    <span>請輸入{{ errorFormColumns.join('、') }}後，再重新送出</span>
                  </div>
                </div>
                <div class="col-12">
                  <label for="project-form" class="form-label">專案名稱</label>
                  <multiselect
                    v-model="projectFormValue"
                    :options="projectFormOptions"
                    label="name"
                    track-by="id"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="🔍  輸入關鍵字搜尋專案 ..."
                    id="project-form"
                  ><span slot="noResult">找不到符合的專案</span></multiselect>
                </div>
                <div class="col-12 col-xl-6">
                  <label for="date-form" class="form-label">日期</label>
                  <DatePicker
                    v-model="dateFormValue"
                    :masks="{ title: 'YYYY年 MMMM' }"
                    trim-weeks
                    :max-date="new Date()"
                    is-required
                  >
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="form-control"
                        id="date-form"
                        :value="inputValue"
                        v-on="inputEvents"
                      />
                    </template>
                  </DatePicker>
                </div>
                <div class="col-12 col-xl-6">
                  <label for="duration-hour-form" class="form-label d-block">時數</label>
                  <multiselect
                    v-model="durationHourFormValue"
                    :options="durationHourFormOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="時"
                    class="duration-hour-form"
                    id="duration-hour-form"
                  ><span slot="noResult">🧐</span></multiselect>
                  <span class="duration-separator-form">:</span>
                  <multiselect
                    v-model="durationMinuteFormValue"
                    :options="durationMinuteFormOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="分"
                    class="duration-minute-form"
                    id="duration-minute-form"
                  ><span slot="noResult">🧐</span></multiselect>
                </div>
                <div class="col-12">
                  <label for="service-form" class="form-label">服務項目</label>
                  <multiselect
                    v-model="serviceFormValue"
                    :options="serviceFormOptions"
                    label="name"
                    track-by="id"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="🔍  輸入關鍵字搜尋服務 ..."
                    id="service-form"
                  ><span slot="noResult">找不到符合的服務</span></multiselect>
                </div>
                <div class="col-12">
                  <label for="description-form" class="form-label">說明</label>
                  <textarea
                    v-model="descriptionFormValue" class="form-control"
                    id="description-form" rows="3"
                  ></textarea>
                </div>
                <div class="col-12 mt-4">
                  <button
                    type="submit"
                    class="btn btn-primary w-100"
                  >確定新增</button>
                </div>
              </form>
            </div>
          </div>
        </aside>
      </div>
    </div>
    <nav class="navbar navbar-expand navbar-shadow fixed-bottom d-md-none navbar-light bg-white">
      <div class="container-fluid">
        <div class="d-flex align-items-center my-1">
          <span class="navbar-brand py-0 mx-0">
            <span class="small">總時數</span>&nbsp;
            {{ Math.trunc(totalDuration['*'] / 60) }} 時
            {{ (totalDuration['*'] % 60).toString().padStart(2, '0') }} 分
          </span>
        </div>
        <div class="d-flex align-items-center my-1">
          <button
            type="button"
            class="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#create-time-entry-modal"
          >&#xff0b;&nbsp;&nbsp;&nbsp;新增</button>
        </div>
      </div>
    </nav>
    <!-- 編輯時數 modal start -->
    <div
      class="modal fade" id="edit-time-entry-modal" tabindex="-1"
      data-bs-backdrop="static" data-bs-keyboard="false"
      aria-labelledby="edit-time-entry-modal-label" aria-hidden="true"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-md-down"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="edit-time-entry-modal-label">編輯時數</h5>
            <button
              type="button" class="btn-close"
              data-bs-dismiss="modal" aria-label="取消"
            ></button>
          </div>
          <div class="modal-body">
            <div class="container-fluid">
              <form class="row gx-4 gy-3">
                <div v-show="errorFormEditColumns.length > 0" class="col-12">
                  <div class="alert alert-danger mb-0 d-flex align-items-center" role="alert">
                    <span class="me-12px"><i class="bi bi-exclamation-circle"></i></span>
                    <span>請輸入{{ errorFormEditColumns.join('、') }}後，再重新送出</span>
                  </div>
                </div>
                <div class="col-12">
                  <label for="project-form-edit" class="form-label">專案名稱</label>
                  <multiselect
                    v-model="projectFormEditValue"
                    :options="projectFormOptions"
                    label="name"
                    track-by="id"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="🔍  輸入關鍵字搜尋專案 ..."
                    id="project-form-edit"
                  ><span slot="noResult">找不到符合的專案</span></multiselect>
                </div>
                <div class="col-12 col-xl-6">
                  <label for="date-form-edit" class="form-label">日期</label>
                  <DatePicker
                    v-model="dateFormEditValue"
                    :masks="{ title: 'YYYY年 MMMM' }"
                    trim-weeks
                    :max-date="new Date()"
                    is-required
                  >
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="form-control"
                        id="date-form-edit"
                        :value="inputValue"
                        v-on="inputEvents"
                      />
                    </template>
                  </DatePicker>
                </div>
                <div class="col-12 col-xl-6">
                  <label for="duration-hour-form-edit" class="form-label d-block">時數</label>
                  <multiselect
                    v-model="durationHourFormEditValue"
                    :options="durationHourFormOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="時"
                    class="duration-hour-form"
                    id="duration-hour-form-edit"
                  ><span slot="noResult">🧐</span></multiselect>
                  <span class="duration-separator-form">:</span>
                  <multiselect
                    v-model="durationMinuteFormEditValue"
                    :options="durationMinuteFormOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="分"
                    class="duration-minute-form"
                    id="duration-minute-form-edit"
                  ><span slot="noResult">🧐</span></multiselect>
                </div>
                <div class="col-12">
                  <label for="service-form-edit" class="form-label">服務項目</label>
                  <multiselect
                    v-model="serviceFormEditValue"
                    :options="serviceFormOptions"
                    label="name"
                    track-by="id"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="🔍  輸入關鍵字搜尋服務 ..."
                    id="service-form-edit"
                  ><span slot="noResult">找不到符合的服務</span></multiselect>
                </div>
                <div class="col-12 mb-2">
                  <label for="description-form-edit" class="form-label">說明</label>
                  <textarea
                    v-model="descriptionFormEditValue" class="form-control"
                    id="description-form-edit" rows="3"
                  ></textarea>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">取消</button>
            <button
              type="button" class="btn btn-primary"
              @click.prevent="updateTimeEntry"
            >儲存修改</button>
          </div>
        </div>
      </div>
    </div>
    <!-- 編輯時數 modal end -->
    <!-- 新增時數 (小網) modal start -->
    <div
      class="modal fade" id="create-time-entry-modal" tabindex="-1"
      data-bs-backdrop="static" data-bs-keyboard="false"
      aria-labelledby="create-time-entry-modal-label" aria-hidden="true"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-md-down"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="create-time-entry-modal-label">新增時數</h5>
            <button
              type="button" class="btn-close"
              data-bs-dismiss="modal" aria-label="取消"
            ></button>
          </div>
          <div class="modal-body">
            <div class="container-fluid">
              <form class="row gx-4 gy-3">
                <div v-show="errorFormCreateColumns.length > 0" class="col-12">
                  <div class="alert alert-danger mb-0 d-flex align-items-center" role="alert">
                    <span class="me-12px"><i class="bi bi-exclamation-circle"></i></span>
                    <span>請輸入{{ errorFormCreateColumns.join('、') }}後，再重新送出</span>
                  </div>
                </div>
                <div class="col-12">
                  <label for="project-form-create" class="form-label">專案名稱</label>
                  <multiselect
                    v-model="projectFormCreateValue"
                    :options="projectFormOptions"
                    label="name"
                    track-by="id"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="🔍  輸入關鍵字搜尋專案 ..."
                    id="project-form-create"
                  ><span slot="noResult">找不到符合的專案</span></multiselect>
                </div>
                <div class="col-12 col-xl-6">
                  <label for="date-form-create" class="form-label">日期</label>
                  <DatePicker
                    v-model="dateFormCreateValue"
                    :masks="{ title: 'YYYY年 MMMM' }"
                    trim-weeks
                    :max-date="new Date()"
                    is-required
                  >
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="form-control"
                        id="date-form-create"
                        :value="inputValue"
                        v-on="inputEvents"
                      />
                    </template>
                  </DatePicker>
                </div>
                <div class="col-12 col-xl-6">
                  <label for="duration-hour-form-create" class="form-label d-block">時數</label>
                  <multiselect
                    v-model="durationHourFormCreateValue"
                    :options="durationHourFormOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="時"
                    class="duration-hour-form"
                    id="duration-hour-form-create"
                  ><span slot="noResult">🧐</span></multiselect>
                  <span class="duration-separator-form">:</span>
                  <multiselect
                    v-model="durationMinuteFormCreateValue"
                    :options="durationMinuteFormOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="分"
                    class="duration-minute-form"
                    id="duration-minute-form-create"
                  ><span slot="noResult">🧐</span></multiselect>
                </div>
                <div class="col-12">
                  <label for="service-form-create" class="form-label">服務項目</label>
                  <multiselect
                    v-model="serviceFormCreateValue"
                    :options="serviceFormOptions"
                    label="name"
                    track-by="id"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="🔍  輸入關鍵字搜尋服務 ..."
                    id="service-form-create"
                  ><span slot="noResult">找不到符合的服務</span></multiselect>
                </div>
                <div class="col-12 mb-2">
                  <label for="description-form-create" class="form-label">說明</label>
                  <textarea
                    v-model="descriptionFormCreateValue" class="form-control"
                    id="description-form-create" rows="3"
                  ></textarea>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button" class="btn btn-primary w-100"
              @click.prevent="createTimeEntryFromModal"
            >確定新增</button>
          </div>
        </div>
      </div>
    </div>
    <!-- 新增時數 (小網) modal end -->
    <!-- 刪除時數 modal start -->
    <div
      class="modal fade" id="delete-time-entry-modal" tabindex="-1"
      data-bs-backdrop="static" data-bs-keyboard="false"
      aria-labelledby="delete-time-entry-modal-label" aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="delete-time-entry-modal-label">刪除時數</h5>
            <button
              type="button" class="btn-close"
              data-bs-dismiss="modal" aria-label="取消"
            ></button>
          </div>
          <div class="modal-body">
            確定要刪除這筆時數紀錄嗎？刪除後就找不回來囉！
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">取消</button>
            <button
              type="button" class="btn btn-danger"
              @click.prevent="deleteTimeEntry"
            >確定刪除</button>
          </div>
        </div>
      </div>
    </div>
    <!-- 刪除時數 modal end -->
    <!-- 成功新增時數 toast start -->
    <div class="position-fixed top-0 start-50 translate-middle-x p-2 z-2k">
      <div
        id="create-time-entry-succeeded"
        class="toast align-items-center toast-success"
        role="alert" aria-live="assertive" aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">🎉&nbsp;&nbsp;成功新增時數</div>
          <button
            type="button" class="btn-close me-2 m-auto"
            data-bs-dismiss="toast" aria-label="關閉"
          ></button>
        </div>
      </div>
    </div>
    <!-- 成功新增時數 toast end -->
    <!-- 成功編輯時數 toast start -->
    <div class="position-fixed top-0 start-50 translate-middle-x p-2 z-2k">
      <div
        id="edit-time-entry-succeeded"
        class="toast align-items-center toast-success"
        role="alert" aria-live="assertive" aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">🎉&nbsp;&nbsp;成功編輯時數</div>
          <button
            type="button" class="btn-close me-2 m-auto"
            data-bs-dismiss="toast" aria-label="關閉"
          ></button>
        </div>
      </div>
    </div>
    <!-- 成功編輯時數 toast end -->
    <!-- 成功刪除時數 toast start -->
    <div class="position-fixed top-0 start-50 translate-middle-x p-2 z-2k">
      <div
        id="delete-time-entry-succeeded"
        class="toast align-items-center toast-success"
        role="alert" aria-live="assertive" aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">🗑&nbsp;&nbsp;成功刪除時數</div>
          <button
            type="button" class="btn-close me-2 m-auto"
            data-bs-dismiss="toast" aria-label="關閉"
          ></button>
        </div>
      </div>
    </div>
    <!-- 成功刪除時數 toast end -->
    <!-- 發生錯誤 toast start -->
    <div class="position-fixed top-0 start-50 translate-middle-x p-2 z-2k">
      <div
        id="create-edit-delete-time-entry-failed"
        class="toast align-items-center toast-danger"
        role="alert" aria-live="assertive" aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">😭&nbsp;&nbsp;發生了一點錯誤，請稍後再試</div>
          <button
            type="button" class="btn-close me-2 m-auto"
            data-bs-dismiss="toast" aria-label="關閉"
          ></button>
        </div>
      </div>
    </div>
    <!-- 發生錯誤 toast end -->
    <!-- 搜尋範圍太大 toast start -->
    <div class="position-fixed top-0 start-50 translate-middle-x p-2 z-2k">
      <div
        id="number-of-time-entries-exceeded-limit"
        class="toast align-items-center toast-warning toast-lg"
        role="alert" aria-live="assertive" aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">😥&nbsp;&nbsp;這個搜尋範圍太大囉！系統目前顯示的是符合條件的前 20000 筆時數紀錄</div>
          <button
            type="button" class="btn-close me-2 m-auto"
            data-bs-dismiss="toast" aria-label="關閉"
          ></button>
        </div>
      </div>
    </div>
    <!-- 搜尋範圍太大 toast end -->
    <!-- 正在載入時數紀錄 toast start -->
    <div class="position-fixed top-0 start-50 translate-middle-x p-2 z-2k">
      <div
        id="loading-time-entries"
        class="toast align-items-center toast-primary"
        role="alert" aria-live="assertive" aria-atomic="true"
        data-bs-autohide="false"
      >
        <div class="d-flex">
          <div class="toast-body">⏳&nbsp;&nbsp;正在載入時數紀錄&nbsp;...</div>
          <button
            type="button" class="btn-close me-2 m-auto"
            data-bs-dismiss="toast" aria-label="關閉"
          ></button>
        </div>
      </div>
    </div>
    <!-- 正在載入時數紀錄 toast end -->
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import { Toast, Modal } from 'bootstrap';
import SiteNav from '@/components/SiteNav.vue';
import TimeEntry from '@/components/TimeEntry.vue';
import TimeEntrySummary from '@/components/TimeEntrySummary.vue';
import { fetchApi } from '../utils/index';

/**
 * Convert JavaScript Date object to YYYY-MM-DD string (in local timezone)
 * @param {Date} date
 * @returns {string}
 */
// eslint-disable-next-line no-unused-vars
function formatDateString(date) {
  const offset = date.getTimezoneOffset();
  const dateWithOffset = new Date(date.getTime() - (offset * 60 * 1000));
  return dateWithOffset.toISOString().slice(0, 10);
}

const formatDayOfWeek = [
  '(日)', '(一)', '(二)', '(三)', '(四)', '(五)', '(六)',
];

/**
 * Dynamically generate a color palette. No need to provide
 * the total number of colors in the color palette.
 * Reference: https://stackoverflow.com/a/20129594
 * @param {number} colorNumber The i-th color in the color palette.
 *                             (starts from 0)
 * @returns {string} A CSS color value.
 */
/*
// eslint-disable-next-line no-unused-vars
function generateColorPalette(colorNumber) {
  const goldenAngle = 137.50776405;
  const hue = colorNumber * goldenAngle;
  return `hsl(${hue}, 60%, 75%)`;
}
*/

function generateMaterialColorPalette(colorNumber) {
  const materialDesignColors = [
    '#f44336', '#9c27b0', '#3f51b5', '#03a9f4', '#009688', '#8bc34a', '#ffeb3b', '#ff9800', '#795548', '#607d8b',
    '#e91e63', '#673ab7', '#2196f3', '#00bcd4', '#4caf50', '#cddc39', '#ffc107', '#ff5722', '#9e9e9e',
  ];
  const newColorNumber = colorNumber % materialDesignColors.length;
  return materialDesignColors[newColorNumber];
}

/**
 * Tries to convert `value` to JSON string
 * and store it in browser's session storage.
 * In case of error, it fails silently.
 * @param {string} key - A unique name used to identify values.
 * @param {any} value - A JavaScript object/ primitive value.
 */
function setStorageItem(key, value) {
  try {
    const item = JSON.stringify(value);
    if (typeof item === 'string' && item.length > 0) {
      sessionStorage.setItem(key, item);
    }
  } catch (e) {
    // do nothing
  }
}

/**
 * Retrieves JSON value named `key` from browser's session storage,
 * then tries to rebuild objects (including Date objects) from that value.
 * After retrieval, this function deletes that value from session storage.
 * @param {string} key - A unique name used to identify values.
 * @returns {null|any} - The JavaScript object/ primitive value retrieved.
 *                       In case of error, or if the `key` does not exist,
 *                       it returns `null`.
 */
function getStorageItem(key) {
  const item = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  if (typeof item !== 'string') {
    return null;
  }
  try {
    return JSON.parse(item, (_, value) => (
      (typeof value === 'string'
        && /^[+-]?\d{4,6}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?(?:[+-]\d\d:\d\d|Z)$/.test(value))
        ? new Date(value) : value
    ));
  } catch (e) {
    return null;
  }
}

export default {
  name: 'Home',
  components: {
    Multiselect,
    DatePicker,
    SiteNav,
    TimeEntry,
    TimeEntrySummary,
  },
  data() {
    // populate year/month dropdown menu in search bar
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const yearAndMonthSelectOptions = [];
    let yearAndMonthSelectValue;
    for (let year = 2018; year <= currentYear; year += 1) {
      for (let month = 1; month <= 12; month += 1) {
        // eslint-disable-next-line prefer-template
        const option = year.toString().padStart(4, '0') + '/' + month.toString().padStart(2, '0');
        yearAndMonthSelectOptions.push(option);
        if (year === currentYear && month === currentMonth) {
          yearAndMonthSelectValue = option;
          break;
        }
      }
    }
    if (yearAndMonthSelectOptions.length === 0) {
      const option = '2018/01';
      yearAndMonthSelectOptions.push(option);
      yearAndMonthSelectValue = option;
    }
    /**
     * @typedef TimeEntry
     * @type {object}
     * @property {number} id
     * @property {number} user
     * @property {string} user_first_name
     * @property {string} user_last_name
     * @property {number} project
     * @property {string} project_name
     * @property {number} service
     * @property {string} service_name
     * @property {string} description
     * @property {string} date
     * @property {string} duration
     */
    return {
      projectSelectValue: { id: '*', name: '所有專案' },
      projectSelectOptions: [],
      userSelectValue: { id: '*', first_name: '所有成員', last_name: '' },
      userSelectOptions: [],
      userSelectOptionsCache: [],
      userSelectOptionsLoading: false,
      yearAndMonthSelectValue,
      yearAndMonthSelectOptions,
      /**
       * toggle selection mode <br>
       * true: select year and month <br>
       * false: select date range
       * @type {boolean}
       */
      yearAndMonthOrDateRange: true,
      /**
       * selected date range, defaults to
       * [first day of current month] ~ [last day of current month]
       */
      datePickerSelectValue: {
        start: (() => {
          const d = new Date();
          const y = d.getFullYear();
          const m = d.getMonth();
          const df = new Date(y, m, 1);
          return formatDateString(df);
        })(),
        end: (() => {
          const d = new Date();
          const y = d.getFullYear();
          const m = d.getMonth();
          const dl = new Date(y, m + 1, 0);
          return formatDateString(dl);
        })(),
      },
      datePickerConfig: {
        type: 'string',
        mask: 'YYYY-MM-DD',
      },
      projectFormValue: getStorageItem('projectFormValue') ?? { id: -1, name: '選擇專案' },
      projectFormEditValue: getStorageItem('projectFormEditValue') ?? { id: -1, name: '選擇專案' },
      projectFormCreateValue: getStorageItem('projectFormCreateValue') ?? { id: -1, name: '選擇專案' },
      projectFormOptions: [],
      dateFormValue: getStorageItem('dateFormValue') ?? new Date(),
      dateFormEditValue: getStorageItem('dateFormEditValue') ?? new Date(),
      dateFormCreateValue: getStorageItem('dateFormCreateValue') ?? new Date(),
      durationHourFormValue: getStorageItem('durationHourFormValue') ?? '1',
      durationHourFormEditValue: getStorageItem('durationHourFormEditValue') ?? '1',
      durationHourFormCreateValue: getStorageItem('durationHourFormCreateValue') ?? '1',
      durationHourFormOptions: [
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11',
        '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23',
      ],
      durationMinuteFormValue: getStorageItem('durationMinuteFormValue') ?? '00',
      durationMinuteFormEditValue: getStorageItem('durationMinuteFormEditValue') ?? '00',
      durationMinuteFormCreateValue: getStorageItem('durationMinuteFormCreateValue') ?? '00',
      durationMinuteFormOptions: [
        '00', '05', '10', '15', '20', '25',
        '30', '35', '40', '45', '50', '55',
      ],
      serviceFormValue: getStorageItem('serviceFormValue') ?? { id: -1, name: '選擇服務' },
      serviceFormEditValue: getStorageItem('serviceFormEditValue') ?? { id: -1, name: '選擇服務' },
      serviceFormCreateValue: getStorageItem('serviceFormCreateValue') ?? { id: -1, name: '選擇服務' },
      serviceFormOptions: [],
      descriptionFormValue: getStorageItem('descriptionFormValue') ?? '',
      descriptionFormEditValue: getStorageItem('descriptionFormEditValue') ?? '',
      descriptionFormCreateValue: getStorageItem('descriptionFormCreateValue') ?? '',
      errorFormColumns: [],
      errorFormEditColumns: [],
      errorFormCreateColumns: [],
      lastClickedTimeEntryId: null,
      highlightedTimeEntryId: -1,
      /** @type {TimeEntry[]} */
      timeEntries: [],
      totalDuration: { '*': 0 },
      selectedDate: null,
      loginUser: {
        id: -1,
        first_name: '',
        last_name: '',
        role: '',
      },
    };
  },
  computed: {
    dateRangeSelectValue() {
      if (this.yearAndMonthOrDateRange) {
        const tokens = this.yearAndMonthSelectValue.split('/');
        const year = parseInt(tokens[0], 10);
        const month = parseInt(tokens[1], 10);
        const numberOfDaysInMonth = new Date(year, month, 0).getDate();
        const start = `${tokens[0]}-${tokens[1]}-01`; // eslint-disable-next-line no-multi-spaces
        const end   = `${tokens[0]}-${tokens[1]}-${numberOfDaysInMonth}`;
        return { start, end };
      } else { // eslint-disable-line no-else-return
        const { start, end } = this.datePickerSelectValue;
        return { start, end };
      }
    },
    isVeryFirstMonth() {
      return (this.yearAndMonthSelectValue
          === this.yearAndMonthSelectOptions[0]);
    },
    isVeryLastMonth() {
      return (this.yearAndMonthSelectValue
          === this.yearAndMonthSelectOptions[this.yearAndMonthSelectOptions.length - 1]);
    },
  },
  methods: {
    scrollToSelectedOption() {
      const selector = '.multiselect.year-and-month-select .multiselect__option.multiselect__option--selected';
      const element = document.querySelector(selector);
      if (typeof element.scrollIntoViewIfNeeded === 'function') {
        // only supported by WebKit
        setTimeout(() => element.scrollIntoViewIfNeeded(true), 50);
      } else {
        // other browsers
        setTimeout(() => element.scrollIntoView({ block: 'center' }), 50);
      }
    },
    displayErrorToast(error) {
      const toastElement = document.querySelector('#create-edit-delete-time-entry-failed');
      const toast = Toast.getOrCreateInstance(toastElement);
      toast.show();
      // eslint-disable-next-line no-console
      console.error('Error Details:', error);
    },
    processTimeEntries() {
      let numberOfTimeEntriesExceededLimit = false;
      if (this.timeEntries.length > 20000) {
        numberOfTimeEntriesExceededLimit = true;
        this.timeEntries.splice(20000);
      }
      let lastDate = null;
      const newTimeEntries = [];
      const newTotalDuration = { '*': 0 };
      const userColorMap = new Map();
      // eslint-disable-next-line no-restricted-syntax
      for (const timeEntry of this.timeEntries) {
        // set new property `highlighted`
        const highlighted = (timeEntry.id === this.highlightedTimeEntryId);
        // set new property `userColor`
        const userColorMapKey = timeEntry.user_first_name + timeEntry.user_last_name;
        let userColor;
        if (userColorMap.has(userColorMapKey)) {
          userColor = userColorMap.get(userColorMapKey);
        } else {
          userColor = generateMaterialColorPalette(userColorMap.size);
          userColorMap.set(userColorMapKey, userColor);
        }
        // set new property `sameDateAsAbove`
        const sameDateAsAbove = (lastDate === timeEntry.date);
        lastDate = timeEntry.date;
        // simple way to convert YYYY-MM-DD to YYYY/MM/DD
        let dateTokens = timeEntry.date.split('-');
        timeEntry.date = dateTokens.join('/');
        // get formatted day of week
        dateTokens = dateTokens.map((t) => parseInt(t, 10));
        const dateObject = new Date(dateTokens[0], dateTokens[1] - 1, dateTokens[2]);
        const dayOfWeek = formatDayOfWeek[dateObject.getDay()];
        // initialize new date entry for `totalDuration`
        if (!Object.prototype.hasOwnProperty.call(newTotalDuration, timeEntry.date)) {
          newTotalDuration[timeEntry.date] = 0;
        }
        // simple way to convert HH:MM:SS to H:MM (no leading zero in hour)
        const durationTokens = timeEntry.duration.split(':');
        durationTokens[0] = (durationTokens[0].startsWith('0'))
          ? durationTokens[0].slice(1)
          : durationTokens[0];
        timeEntry.duration = `${durationTokens[0]} : ${durationTokens[1]}`;
        // add up duration of time entries
        const durationInMinutes = parseInt(durationTokens[0], 10) * 60
          + parseInt(durationTokens[1], 10);
        newTotalDuration[timeEntry.date] += durationInMinutes;
        newTotalDuration['*'] += durationInMinutes;
        // finished, rebuild the time entry object
        // eslint-disable-next-line object-curly-newline
        newTimeEntries.push({ ...timeEntry, highlighted, userColor, sameDateAsAbove, dayOfWeek });
      }
      this.timeEntries = newTimeEntries;
      this.totalDuration = newTotalDuration;
      this.highlightedTimeEntryId = -1;
      if (numberOfTimeEntriesExceededLimit) {
        const toastElement = document.querySelector('#number-of-time-entries-exceeded-limit');
        const toast = Toast.getOrCreateInstance(toastElement);
        toast.show();
      }
    },
    filterTimeEntriesByUser(selectedValue) {
      const parameters = {
        user: selectedValue.id,
        project: this.projectSelectValue.id,
        start: this.dateRangeSelectValue.start,
        end: this.dateRangeSelectValue.end,
      };
      this.reloadTimeEntries(parameters);
    },
    filterTimeEntriesByProject(selectedValue) {
      // 當使用者選取「所有專案」時，成員選單自動選取使用者的名字；當使用者選取特定專案
      // 時，成員選單自動選取「所有成員」，新增時數表單中的專案名稱選單自動選取相同的專案
      this.fetchProjectUsers(selectedValue.id);
      if (this.projectFormOptions.some((project) => project.id === selectedValue.id)) {
        this.projectFormValue = selectedValue;
        this.projectFormCreateValue = selectedValue;
      } else {
        this.projectFormValue = { id: -1, name: '選擇專案' };
        this.projectFormCreateValue = { id: -1, name: '選擇專案' };
      }
      const parameters = {
        user: this.userSelectValue.id,
        project: selectedValue.id,
        start: this.dateRangeSelectValue.start,
        end: this.dateRangeSelectValue.end,
      };
      this.reloadTimeEntries(parameters);
    },
    /*
    filterTimeEntriesByYearAndMonth(selectedValue) {
      const parameters = {
        user: this.userSelectValue.id,
        project: this.projectSelectValue.id,
        year: parseInt(selectedValue.split('/')[0], 10),
        month: parseInt(selectedValue.split('/')[1], 10),
      };
      this.reloadTimeEntries(parameters);
    },
    */
    filterTimeEntriesByDateRange(selectedValue) {
      const parameters = {
        user: this.userSelectValue.id,
        project: this.projectSelectValue.id,
        start: selectedValue.start,
        end: selectedValue.end,
      };
      this.reloadTimeEntries(parameters);
      const defaultDate = new Date(`${selectedValue.start}T12:00:00`);
      this.dateFormValue = defaultDate;
      this.dateFormCreateValue = defaultDate;
      this.dateFormEditValue = defaultDate;
    },
    reloadTimeEntries(parameters) {
      if (parameters === undefined) {
        // eslint-disable-next-line no-param-reassign
        parameters = {
          user: this.userSelectValue.id,
          project: this.projectSelectValue.id,
          start: this.dateRangeSelectValue.start,
          end: this.dateRangeSelectValue.end,
        };
      }
      const toastElement = document.querySelector('#loading-time-entries');
      const loadingToast = Toast.getOrCreateInstance(toastElement);
      loadingToast.show();
      fetchApi('getTimeEntries', parameters)
        .then((data) => {
          if (data.status === -1) {
            loadingToast.hide();
            this.$router.push('/login');
          }
          if (data.status !== 0) {
            throw new Error(`${data.message} (${data.status})`);
          }
          this.timeEntries = data.results;
          this.processTimeEntries();
          loadingToast.hide();
        })
        .catch((error) => {
          loadingToast.hide();
          this.displayErrorToast(error);
        });
    },
    fetchProjectUsers(projectId) {
      if (projectId === undefined || projectId === '*') {
        this.userSelectOptions = (this.loginUser.role === 'Dir')
          ? [...this.userSelectOptionsCache]
          : [this.loginUser];
        this.userSelectValue = this.loginUser;
        return;
      }
      this.userSelectOptionsLoading = true;
      const parameters = {
        project: projectId,
      };
      const allUsers = {
        id: '*',
        first_name: '所有成員',
        last_name: '',
      };
      fetchApi('getUsers', parameters)
        .then((data) => {
          if (data.status === -1) {
            this.$router.push('/login');
          }
          if (data.status !== 0) {
            throw new Error(`${data.message} (${data.status})`);
          }
          const results = data.results.filter((user) => user.id !== this.loginUser.id);
          this.userSelectOptions = [allUsers, this.loginUser, ...results];
          this.userSelectOptionsLoading = false;
        })
        .catch((error) => {
          this.displayErrorToast(error);
        });
      this.userSelectValue = allUsers;
    },
    createTimeEntry() {
      const offset = this.dateFormValue.getTimezoneOffset();
      const dateWithOffset = new Date(this.dateFormValue.getTime() - (offset * 60 * 1000));
      const parameters = {
        project: this.projectFormValue.id,
        date: dateWithOffset.toISOString().slice(0, 10),
        duration: `${this.durationHourFormValue.padStart(2, '0')}:${this.durationMinuteFormValue}:00`,
        service: this.serviceFormValue.id,
        description: this.descriptionFormValue,
      };
      const errorFormColumns = [];
      if (parameters.project === -1) errorFormColumns.push('專案名稱');
      if (parameters.duration === '00:00:00') errorFormColumns.push('時數');
      if (parameters.service === -1) errorFormColumns.push('服務項目');
      if (errorFormColumns.length > 0) {
        this.errorFormColumns = errorFormColumns;
        return;
      }
      this.errorFormColumns = [];
      fetchApi('createTimeEntry', parameters)
        .then((data) => {
          if (data.status === -1) {
            setStorageItem('projectFormValue', this.projectFormValue);
            setStorageItem('dateFormValue', this.dateFormValue);
            setStorageItem('durationHourFormValue', this.durationHourFormValue);
            setStorageItem('durationMinuteFormValue', this.durationMinuteFormValue);
            setStorageItem('serviceFormValue', this.serviceFormValue);
            setStorageItem('descriptionFormValue', this.descriptionFormValue);
            this.$router.push('/login');
          }
          if (data.status !== 0) {
            throw new Error(`${data.message} (${data.status})`);
          }
          this.highlightedTimeEntryId = data.last_insert_id;
          this.reloadTimeEntries();
          // 新增時數後，專案名稱、日期這兩個欄位不要重置，方便使用者批次新增時數
          // this.projectFormValue = { id: -1, name: '選擇專案' };
          // this.dateFormValue = new Date();
          this.durationHourFormValue = '1';
          this.durationMinuteFormValue = '00';
          this.serviceFormValue = { id: -1, name: '選擇服務' };
          this.descriptionFormValue = '';
          const toastElement = document.querySelector('#create-time-entry-succeeded');
          const toast = Toast.getOrCreateInstance(toastElement);
          toast.show();
        })
        .catch((error) => {
          this.displayErrorToast(error);
        });
    },
    createTimeEntryFromModal() {
      const offset = this.dateFormCreateValue.getTimezoneOffset();
      const dateWithOffset = new Date(this.dateFormCreateValue.getTime() - (offset * 60 * 1000));
      const parameters = {
        project: this.projectFormCreateValue.id,
        date: dateWithOffset.toISOString().slice(0, 10),
        duration: `${this.durationHourFormCreateValue.padStart(2, '0')}:${this.durationMinuteFormCreateValue}:00`,
        service: this.serviceFormCreateValue.id,
        description: this.descriptionFormCreateValue,
      };
      const errorFormColumns = [];
      if (parameters.project === -1) errorFormColumns.push('專案名稱');
      if (parameters.duration === '00:00:00') errorFormColumns.push('時數');
      if (parameters.service === -1) errorFormColumns.push('服務項目');
      if (errorFormColumns.length > 0) {
        this.errorFormCreateColumns = errorFormColumns;
        return;
      }
      this.errorFormCreateColumns = [];
      fetchApi('createTimeEntry', parameters)
        .then((data) => {
          if (data.status === -1) {
            setStorageItem('projectFormCreateValue', this.projectFormCreateValue);
            setStorageItem('dateFormCreateValue', this.dateFormCreateValue);
            setStorageItem('durationHourFormCreateValue', this.durationHourFormCreateValue);
            setStorageItem('durationMinuteFormCreateValue', this.durationMinuteFormCreateValue);
            setStorageItem('serviceFormCreateValue', this.serviceFormCreateValue);
            setStorageItem('descriptionFormCreateValue', this.descriptionFormCreateValue);
            this.$router.push('/login');
          }
          if (data.status !== 0) {
            throw new Error(`${data.message} (${data.status})`);
          }
          this.highlightedTimeEntryId = data.last_insert_id;
          this.reloadTimeEntries();
          const modalElement = document.querySelector('#create-time-entry-modal');
          const modal = Modal.getOrCreateInstance(modalElement);
          modal.hide();
          // 新增時數後，專案名稱、日期這兩個欄位不要重置，方便使用者批次新增時數
          // this.projectFormCreateValue = { id: -1, name: '選擇專案' };
          // this.dateFormCreateValue = new Date();
          this.durationHourFormCreateValue = '1';
          this.durationMinuteFormCreateValue = '00';
          this.serviceFormCreateValue = { id: -1, name: '選擇服務' };
          this.descriptionFormCreateValue = '';
          const toastElement = document.querySelector('#create-time-entry-succeeded');
          const toast = Toast.getOrCreateInstance(toastElement);
          toast.show();
        })
        .catch((error) => {
          this.displayErrorToast(error);
        });
    },
    updateTimeEntry() {
      const offset = this.dateFormEditValue.getTimezoneOffset();
      const dateWithOffset = new Date(this.dateFormEditValue.getTime() - (offset * 60 * 1000));
      const parameters = {
        id: this.lastClickedTimeEntryId,
        project: this.projectFormEditValue.id,
        date: dateWithOffset.toISOString().slice(0, 10),
        duration: `${this.durationHourFormEditValue.padStart(2, '0')}:${this.durationMinuteFormEditValue}:00`,
        service: this.serviceFormEditValue.id,
        description: this.descriptionFormEditValue,
      };
      const errorFormColumns = [];
      if (parameters.project === -1) errorFormColumns.push('專案名稱');
      if (parameters.duration === '00:00:00') errorFormColumns.push('時數');
      if (parameters.service === -1) errorFormColumns.push('服務項目');
      if (errorFormColumns.length > 0) {
        this.errorFormEditColumns = errorFormColumns;
        return;
      }
      this.errorFormEditColumns = [];
      fetchApi('updateTimeEntry', parameters)
        .then((data) => {
          if (data.status === -1) {
            setStorageItem('projectFormEditValue', this.projectFormEditValue);
            setStorageItem('dateFormEditValue', this.dateFormEditValue);
            setStorageItem('durationHourFormEditValue', this.durationHourFormEditValue);
            setStorageItem('durationMinuteFormEditValue', this.durationMinuteFormEditValue);
            setStorageItem('serviceFormEditValue', this.serviceFormEditValue);
            setStorageItem('descriptionFormEditValue', this.descriptionFormEditValue);
            this.$router.push('/login');
          }
          if (data.status !== 0) {
            throw new Error(`${data.message} (${data.status})`);
          }
          this.highlightedTimeEntryId = parameters.id;
          this.reloadTimeEntries();
          const modalElement = document.querySelector('#edit-time-entry-modal');
          const modal = Modal.getOrCreateInstance(modalElement);
          modal.hide();
          this.projectFormEditValue = { id: -1, name: '選擇專案' };
          this.dateFormEditValue = new Date();
          this.durationHourFormEditValue = '1';
          this.durationMinuteFormEditValue = '00';
          this.serviceFormEditValue = { id: -1, name: '選擇服務' };
          this.descriptionFormEditValue = '';
          const toastElement = document.querySelector('#edit-time-entry-succeeded');
          const toast = Toast.getOrCreateInstance(toastElement);
          toast.show();
        })
        .catch((error) => {
          this.displayErrorToast(error);
        });
    },
    deleteTimeEntry() {
      const parameters = {
        id: this.lastClickedTimeEntryId,
      };
      fetchApi('deleteTimeEntry', parameters)
        .then((data) => {
          if (data.status === -1) {
            this.$router.push('/login');
          }
          if (data.status !== 0) {
            throw new Error(`${data.message} (${data.status})`);
          }
          this.reloadTimeEntries();
          const modalElement = document.querySelector('#delete-time-entry-modal');
          const modal = Modal.getOrCreateInstance(modalElement);
          modal.hide();
          const toastElement = document.querySelector('#delete-time-entry-succeeded');
          const toast = Toast.getOrCreateInstance(toastElement);
          toast.show();
        })
        .catch((error) => {
          this.displayErrorToast(error);
        });
    },
    copyTimeEntry() {
      let lastClickedTimeEntry = this.timeEntries[0];
      // eslint-disable-next-line no-restricted-syntax
      for (const timeEntry of this.timeEntries) {
        if (timeEntry.id === this.lastClickedTimeEntryId) {
          lastClickedTimeEntry = timeEntry;
          break;
        }
      }
      if (this.projectFormOptions.some(
        (project) => project.id === lastClickedTimeEntry.project,
      )) {
        this.projectFormValue = {
          id: lastClickedTimeEntry.project,
          name: lastClickedTimeEntry.project_name,
        };
      } else {
        this.projectFormValue = {
          id: -1,
          name: '選擇專案',
        };
      }
      const dateTokens = lastClickedTimeEntry.date.split('/').map((t) => parseInt(t, 10));
      dateTokens[1] -= 1; // JavaScript uses 0-11 to represent month 1-12
      this.dateFormValue = new Date(dateTokens[0], dateTokens[1], dateTokens[2]);
      // eslint-disable-next-line operator-linebreak
      [this.durationHourFormValue, this.durationMinuteFormValue] =
        lastClickedTimeEntry.duration.split(' : ');
      this.serviceFormValue = {
        id: lastClickedTimeEntry.service,
        name: lastClickedTimeEntry.service_name,
      };
      this.descriptionFormValue = lastClickedTimeEntry.description;
      // 使用小網
      const asideElement = document.querySelector('aside');
      const asideStyles = window.getComputedStyle(asideElement);
      const asideDisplay = asideStyles.getPropertyValue('display');
      if (asideDisplay === 'none') {
        this.projectFormCreateValue = this.projectFormValue;
        this.dateFormCreateValue = this.dateFormValue;
        this.durationHourFormCreateValue = this.durationHourFormValue;
        this.durationMinuteFormCreateValue = this.durationMinuteFormValue;
        this.serviceFormCreateValue = this.serviceFormValue;
        this.descriptionFormCreateValue = this.descriptionFormValue;
        const modalElement = document.querySelector('#create-time-entry-modal');
        const modal = Modal.getOrCreateInstance(modalElement);
        modal.show();
      }
    },
    goPrevMonth() {
      const currIndex = this.yearAndMonthSelectOptions.indexOf(this.yearAndMonthSelectValue);
      if (currIndex > 0) {
        const targetValue = this.yearAndMonthSelectOptions[currIndex - 1];
        this.yearAndMonthSelectValue = targetValue;
      }
    },
    goNextMonth() {
      const currIndex = this.yearAndMonthSelectOptions.indexOf(this.yearAndMonthSelectValue);
      if (currIndex >= 0 && currIndex < this.yearAndMonthSelectOptions.length - 1) {
        const targetValue = this.yearAndMonthSelectOptions[currIndex + 1];
        this.yearAndMonthSelectValue = targetValue;
      }
    },
  },
  watch: {
    yearAndMonthSelectValue: {
      handler() {
        this.selectedDate = null;
      },
    },
    yearAndMonthOrDateRange: {
      handler() {
        this.selectedDate = null;
      },
    },
    dateRangeSelectValue: {
      handler(selectedValue) {
        this.filterTimeEntriesByDateRange(selectedValue);
      },
      deep: true,
    },
    selectedDate: {
      handler(dateString) {
        if (dateString === null) return;
        // eslint-disable-next-line no-param-reassign
        dateString = dateString.split('/').join('-');
        const defaultDate = new Date(`${dateString}T12:00:00`);
        this.dateFormValue = defaultDate;
        this.dateFormCreateValue = defaultDate;
        this.dateFormEditValue = defaultDate;
      },
    },
  },
  mounted() {
    const toastElement = document.querySelector('#loading-time-entries');
    const loadingToast = Toast.getOrCreateInstance(toastElement);
    fetchApi('getLoggedInUser')
      .then((data) => {
        if (data.status === -1) {
          this.$router.push('/login');
        }
        if (data.status !== 0) {
          throw new Error(`${data.message} (${data.status})`);
        }
        const { results } = data;
        [this.loginUser] = results;
        this.userSelectOptions = results;
        [this.userSelectValue] = results;
        loadingToast.show();
      })
      .then(() => Promise.all([
        fetchApi('getTimeEntries', {
          user: this.userSelectValue.id,
          project: this.projectSelectValue.id,
          start: this.dateRangeSelectValue.start,
          end: this.dateRangeSelectValue.end,
        }),
        fetchApi('getProjects'),
        fetchApi('getServices'),
        (this.loginUser.role === 'Dir')
          ? fetchApi('getAllUsers')
          : { status: 0, message: 'OK', results: [] },
      ]))
      .then((dataList) => {
        const resultsList = [];
        let projectOptions;
        let newProjectOptions;
        dataList.forEach((data) => {
          if (data.status === -1) {
            loadingToast.hide();
            this.$router.push('/login');
          }
          if (data.status !== 0) {
            throw new Error(`${data.message} (${data.status})`);
          }
          resultsList.push(data.results);
        });
        [this.timeEntries, projectOptions, this.serviceFormOptions] = resultsList;
        newProjectOptions = projectOptions.map((project) => ({
          id: project.id,
          name: (project.is_active) ? project.name : `🔒  ${project.name}`,
        }));
        this.projectSelectOptions = [{ id: '*', name: '所有專案' }, ...newProjectOptions];
        newProjectOptions = projectOptions.filter((project) => project.is_active);
        this.projectFormOptions = newProjectOptions.map((project) => ({
          id: project.id,
          name: project.name,
        }));
        if (this.loginUser.role === 'Dir') {
          let users = resultsList[3];
          users = users.map((user) => ({
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
          }));
          users = users.sort((userA, userB) => {
            const nameA = `${userA.first_name} ${userA.last_name}`;
            const nameB = `${userB.first_name} ${userB.last_name}`;
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
          });
          users = users.filter((user) => user.is_active && user.id !== this.loginUser.id);
          const allUsers = {
            id: '*',
            first_name: '所有成員',
            last_name: '',
          };
          this.userSelectOptionsCache = [allUsers, this.loginUser, ...users];
          this.userSelectOptions = [...this.userSelectOptionsCache];
        }
        this.processTimeEntries();
        loadingToast.hide();
      })
      .catch((error) => {
        loadingToast.hide();
        this.displayErrorToast(error);
      });
    const modalElement = document.querySelector('#edit-time-entry-modal');
    modalElement.addEventListener('show.bs.modal', () => {
      let lastClickedTimeEntry = this.timeEntries[0];
      // eslint-disable-next-line no-restricted-syntax
      for (const timeEntry of this.timeEntries) {
        if (timeEntry.id === this.lastClickedTimeEntryId) {
          lastClickedTimeEntry = timeEntry;
          break;
        }
      }
      this.projectFormEditValue = {
        id: lastClickedTimeEntry.project,
        name: lastClickedTimeEntry.project_name,
      };
      const dateTokens = lastClickedTimeEntry.date.split('/').map((t) => parseInt(t, 10));
      dateTokens[1] -= 1; // JavaScript uses 0-11 to represent month 1-12
      this.dateFormEditValue = new Date(dateTokens[0], dateTokens[1], dateTokens[2]);
      // eslint-disable-next-line operator-linebreak
      [this.durationHourFormEditValue, this.durationMinuteFormEditValue] =
        lastClickedTimeEntry.duration.split(' : ');
      this.serviceFormEditValue = {
        id: lastClickedTimeEntry.service,
        name: lastClickedTimeEntry.service_name,
      };
      this.descriptionFormEditValue = lastClickedTimeEntry.description;
    });
    modalElement.addEventListener('hidden.bs.modal', () => {
      this.errorFormEditColumns = [];
    });
    const exportTimeEntryButton = document.querySelector('#export-time-entry-button');
    exportTimeEntryButton.addEventListener('click', () => {
      exportTimeEntryButton.disabled = true;
      import('json2csv').then((Module) => {
        const transformTimeEntry = (item) => ({
          ...item,
          user_full_name: `${item.user_first_name} ${item.user_last_name}`,
          duration: (() => {
            const tokens = item.duration.split(':');
            const minutes = parseInt(tokens[0], 10) * 60 + parseInt(tokens[1], 10);
            const hours = minutes / 60;
            return hours.toFixed(2);
          })(),
        });
        const parserOptions = {
          fields: [
            {
              label: '流水號',
              value: 'id',
            },
            {
              label: '日期',
              value: 'date',
            },
            {
              label: '專案名稱',
              value: 'project_name',
            },
            {
              label: '服務項目',
              value: 'service_name',
            },
            {
              label: '說明',
              value: 'description',
            },
            {
              label: '成員',
              value: 'user_full_name',
            },
            {
              label: '時數',
              value: 'duration',
            },
          ],
          transforms: [
            transformTimeEntry,
          ],
        };
        const timeEntries = [...this.timeEntries].sort((a, b) => {
          /* eslint-disable no-multi-spaces, space-in-parens */
          // time entries in the exported csv should be ordered by
          // project (ascending) firstly, and user (ascending) secondly
          if (a.project_name    < b.project_name   ) return -1;
          if (a.project_name    > b.project_name   ) return  1;
          if (a.user_first_name < b.user_first_name) return -1;
          if (a.user_first_name > b.user_first_name) return  1;
          if (a.user_last_name  < b.user_last_name ) return -1;
          if (a.user_last_name  > b.user_last_name ) return  1;
          return 0;
          /* eslint-enable no-multi-spaces, space-in-parens */
        });
        Module.parseAsync(timeEntries, parserOptions)
          .then((csv) => {
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = 'TimeEntries.csv';
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(url);
            exportTimeEntryButton.disabled = false;
          })
          .catch((error) => {
            this.displayErrorToast(error);
            exportTimeEntryButton.disabled = false;
          });
      }).catch((error) => {
        this.displayErrorToast(error);
        exportTimeEntryButton.disabled = false;
      });
    });
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
.text-primary {
  color: #0069b7 !important;
  &:hover {
    color: #0b5ed7 !important;
  }
}
.navbar-dark.bg-primary {
  background-color: #0069b7 !important;
}
.card.card-shadow {
  box-shadow: 1px 1px 5px 1px rgba(0, 0, 0, 0.125);
  border-width: 0;
}
.btn.btn-primary {
  background-color: #0069b7;
  border-color: #0069b7;
  &:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
  }
}
.btn.btn-outline-primary {
  color: #0069b7;
  border-color: #0069b7;
  &:hover {
    color: #fff;
    background-color: #0069b7;
    border-color: #0069b7;
  }
}
.bi::before {
  font-size: 1.25em;
  vertical-align: -0.2em;
}
.px-2\.5 {
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
}
.py-4\.5 {
  padding-top: 2rem !important;
  padding-bottom: 2rem !important;
}
.mb-6 {
  margin-bottom: 4.5rem !important;
}
.me-12px {
  margin-right: 12px !important;
}
.w-450px {
  width: 450px;
}
.min-w-auto {
  min-width: auto;
}
.max-w-482px {
  max-width: 482px;
}
.max-w-300px {
  max-width: 300px;
  @media (max-width: 767.98px) {
    max-width: none;
  }
}
.cursor-reset {
  cursor: default !important;
}
.z-2k {
  z-index: 2000;
}
.modal .modal-body .container-fluid {
  padding-left: 0;
  padding-right: 0;
}
.modal-header {
  border-bottom-width: 0;
}
.modal-title {
  font-weight: 700;
}
.modal-footer {
  border-top-width: 0;
}
.toast-success {
  background-color: rgba(#d4edda, 1);
  border-color: #c3e6cb;
  color: #155724;
}
.toast-danger {
  background-color: rgba(#f8d7da, 1);
  border-color: #f5c6cb;
  color: #721c24;
}
.toast-warning {
  background-color: rgba(#fff3cd, 1);
  border-color: #ffecb5;
  color: #664d03;
}
.toast-primary {
  background-color: rgba(#cce5ff, 1);
  border-color: #b8daff;
  color: #004085;
}
.toast-lg {
  width: 515px;
  max-width: 95vw;
}
</style>

<style scoped lang="scss">
@import "~bootstrap/scss/bootstrap-grid";
.container-fluid {
  @include media-breakpoint-up(md) {
    padding-left: 48px;
    padding-right: 48px;
  }
}
.container-3xl {
  @media (min-width: 1441px) {
    max-width: 1440px;
  }
}
.small {
  vertical-align: 0.05em;
}
.navbar .container-fluid .row .col {
  margin-top: 4px;
  margin-bottom: 4px;
}
.navbar.navbar-shadow {
  box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.125);
}
#export-time-entry-button {
  font-size: 1rem;
  height: 38px;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
#export-time-entry-button .spinner-border {
  display: none;
}
#export-time-entry-button:disabled .spinner-border {
  display: inline-block;
}
.multiselect.project-select {
  width: 100%;
  @include media-breakpoint-up(md) {
    width: 320px;
  }
}
.multiselect.user-select {
  width: 100%;
  @include media-breakpoint-up(md) {
    width: 160px;
  }
}
.multiselect.year-and-month-select {
  width: 100%;
  @include media-breakpoint-up(md) {
    width: 148px;
    ::v-deep .multiselect__tags {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left-width: 0;
    }
  }
}
.form-control.date-range-select {
  width: 120px;
  height: 40px;
  &:first-of-type {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-width: 0;
  }
}
::v-deep .multiselect__single {
  overflow: hidden;
  white-space: pre;
}
::v-deep .multiselect__option {
  white-space: pre;
}
::v-deep .multiselect__select:before {
  top: 72%;
}
::v-deep .multiselect__content-wrapper {
  overflow-x: hidden;
}
::v-deep .multiselect__tags {
  max-height: 40px;
}
.text-multiline {
  white-space: normal;
  line-height: 1.5;
}
.form-control {
  border-color: #e8e8e8;
}
.input-group-text {
  border-color: #e8e8e8;
  background-color: rgba(var(--bs-light-rgb), 1);
  width: 40px;
  height: 40px;
}
.toggle-date-range {
  border: 1px solid #e8e8e8;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
  margin-left: 12px;
  margin-right: -12px;
  z-index: 2000;
  &:hover {
    background-color: rgba(0, 0, 0, 0.075);
  }
}
.table>:not(:first-child) {
  border-top-width: 0;
}
.card-body {
  padding: 40px;
  .card-title {
    margin-bottom: 1.5rem;
  }
}
.sticky-after-navbar {
  @include media-breakpoint-up(md) {
    top: 88px;
    z-index: 1;
  }
}
.duration-separator-form {
  width: 2em;
  text-align: center;
  display: inline-block;
}
.duration-hour-form,
.duration-minute-form {
  width: calc( 50% - 1em );
  display: inline-block;
}
.margin-with-bottom-navbar {
  margin-top: 1.5rem;
  margin-bottom: 5.5rem;
  @include media-breakpoint-up(md) {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }
}
.btn-outline-secondary {
  border-color: rgb(230, 230, 231);
  box-shadow: none;
  &:hover {
    background-color: rgba(0, 0, 0, 0.075);
  }
}
.bi-chevron-left::before,
.bi-chevron-right::before {
  color: #212529;
}
.btn-outline-secondary.disabled,
.btn-outline-secondary:disabled {
  .bi-chevron-left::before,
  .bi-chevron-right::before {
    color: #6c757d;
  }
}
</style>
